import { isApiError, NetworkError } from '@shared/api/errors/errors';
import { readApiError } from '@shared/api/errors/read-api-error';

export interface RbacErrorCopy {
  key: string;
  values?: Record<string, string>;
}

const CODE_KEYS: Record<string, string> = {
  WITHDRAWAL_SELF_APPROVAL: 'errors.selfApproval',
  WITHDRAWAL_MAKER_UNKNOWN: 'errors.makerUnknown',
  WITHDRAWAL_NOT_PENDING: 'errors.notPending',
  WITHDRAWAL_NOT_CANCELLABLE: 'errors.notCancellable',
  WITHDRAWAL_VERSION_CONFLICT: 'errors.versionConflict',
  WITHDRAWAL_INVALID_STATE: 'errors.invalidState',
  WITHDRAWAL_OWNER_DELETED: 'errors.ownerDeleted',
  WITHDRAWAL_WALLET_SCOPE: 'errors.walletScope',
  WITHDRAWAL_BALANCE: 'errors.balance',
  WITHDRAWALS_LOCKED: 'errors.locked',
  ADDRESS_NOT_WHITELISTED: 'errors.address',
  EMERGENCY_GRANT_INVALID: 'errors.grantInvalid',
  EMERGENCY_GRANT_EXISTS: 'errors.grantExists',
  EMERGENCY_GRANT_NOT_REVOCABLE: 'errors.grantNotRevocable',
  IDEMPOTENCY_CONFLICT: 'errors.idempotency',
  SESSION_REVOKED: 'errors.sessionRevoked',
  PERMISSION_DENIED: 'errors.permissionDenied',
  PROJECT_OWNER_AMBIGUOUS: 'errors.ambiguousOwner',
};

export const isUncertainMutationError = (error: unknown): boolean => {
  if (error instanceof NetworkError) return true;
  if (!isApiError(error)) return false;
  return error.status == null || error.status >= 500;
};

export const rbacErrorCopy = (error: unknown): RbacErrorCopy | null => {
  if (isUncertainMutationError(error)) return { key: 'errors.uncertain' };
  const parsed = readApiError(error);
  if (!parsed?.code) return null;
  if (parsed.code === 'ADDRESS_NOT_WHITELISTED' && parsed.reason === 'pending') {
    return { key: 'errors.addressPending', values: { time: parsed.activeFrom ?? '' } };
  }
  if (parsed.code === 'WITHDRAWALS_LOCKED') {
    return { key: 'errors.locked', values: { time: parsed.lockedUntil ?? '' } };
  }
  const key = CODE_KEYS[parsed.code];
  return key ? { key } : null;
};

export const createIdempotencyKey = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const formatRbacError = (
  error: unknown,
  translate: (key: string, values?: Record<string, string>) => string,
  fallback: string,
): string => {
  const copy = rbacErrorCopy(error);
  if (!copy) {
    if (isApiError(error) && error.message) return error.message;
    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message) {
      return error.message;
    }
    return fallback;
  }
  const time = copy.values?.time;
  return translate(copy.key, { time: time ? ` (${time})` : '' });
};

export const buildAdminCommand = (
  reason: string,
  method: 'totp' | 'backup',
  code: string,
): { ok: true; reason: string; secondFactor: { method: 'totp' | 'backup'; code: string } } | { ok: false; field: 'reason' | 'code' } => {
  const trimmed = reason.trim();
  if (!trimmed || trimmed.length > 1000) return { ok: false, field: 'reason' };
  const factor = code.trim();
  if (!factor) return { ok: false, field: 'code' };
  return { ok: true, reason: trimmed, secondFactor: { method, code: factor } };
};
